import clsx from 'clsx';
import { useMemo } from 'react';
import { SearchStatus } from '../declarations/Select.types.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { InputTag } from '../../inputTag/InputTag.js';
import { isString, isUndefined } from '../../../utils/is.js';
import { InputBase } from '../../input/InputBase.js';

const useRenderSelect = ({
  searchStatus,
  inputValue,
  isRetainInputValueSearch,
  renderedValue,
  placeholder,
  canFocusInput,
  name,
  inputEventHandlers,
  refInput,
  disabled,
  styles,
  value,
  maxTagCount,
  onRemoveCheckedItem,
  showSearch,
  size,
  renderTag,
  label,
  idSuffix,
  hasId,
  optionMap
}) => {
  const renderSingle = () => {
    let _inputValue;
    switch (searchStatus) {
      case SearchStatus.BEFORE:
        _inputValue = inputValue || (isRetainInputValueSearch ? renderedValue : '');
        break;
      case SearchStatus.EDITING:
        _inputValue = inputValue || '';
        break;
      default:
        _inputValue = renderedValue;
        break;
    }
    const mergedPlaceholder = !isString(label) ? placeholder : '';
    const hasValue = canFocusInput && renderedValue && typeof renderedValue !== 'object' || _inputValue;
    const inputProps = {
      style: {
        width: '100%',
        cursor: canFocusInput ? 'text' : 'pointer'
      },
      'aria-label': mergedPlaceholder,
      value: typeof _inputValue !== 'object' ? _inputValue : '',
      // Allow placeholder to display the selected value first when searching
      placeholder: canFocusInput && renderedValue && typeof renderedValue !== 'object' ? renderedValue : mergedPlaceholder,
      name,
      id: hasId ? name + idSuffix : null
    };
    if (canFocusInput) {
      inputProps.onKeyDown = inputEventHandlers.keyDown;
      inputProps.onFocus = inputEventHandlers.focus;
      inputProps.onBlur = inputEventHandlers.blur;
      inputProps.onValueChange = inputEventHandlers.change;
    } else {
      // Avoid input getting focus by Tab
      // Do NOT pass [disabled] to <input>, otherwise the click event will not be triggered
      // https://stackoverflow.com/questions/7833854/jquery-detect-click-on-disabled-submit-button
      inputProps.tabIndex = -1;
      inputProps.style.pointerEvents = 'none';
    }

    // <input> is used to input and display placeholder, in other cases use <span> to display value to support displaying rich text
    const labelElement = /*#__PURE__*/jsx("label", {
      className: clsx(styles.label, hasValue && styles.label_active),
      htmlFor: name + idSuffix,
      "aria-labelledby": name + idSuffix,
      children: label
    });
    return /*#__PURE__*/jsxs(Fragment, {
      children: [isString(label) && labelElement, /*#__PURE__*/jsx(InputBase, {
        ref: refInput,
        disabled: disabled,
        className: clsx(styles.input, {
          [styles.has_label]: isString(label)
        }),
        autoComplete: "off",
        ...inputProps
      })]
    });
  };
  const findValidKeyInOptions = (value, options) => {
    let result = null;
    for (const [key, val] of options) {
      if (val.label === value) {
        result = key;
      }
    }
    return result;
  };
  const renderMultiple = useMemo(() => {
    const usedValue = isUndefined(value) ? [] : [].concat(value);
    const usedMaxTagCount = typeof maxTagCount === 'number' ? Math.max(maxTagCount, 0) : usedValue.length;
    const tagsToShow = usedValue.slice(0, usedMaxTagCount).map(v => {
      const key = findValidKeyInOptions(v, optionMap);
      const option = optionMap.get(key);
      if (!option) return null;
      return {
        value: option.value,
        label: option.label
      };
    }).filter(val => val !== null);
    const invisibleTagCount = usedValue.length - usedMaxTagCount;
    if (invisibleTagCount > 0) {
      tagsToShow.push({
        label: `+${invisibleTagCount}...`,
        // InputTag needs to extract value as key
        value: '_value_tag_placeholder'
      });
    }
    const eventHandlers = {
      onKeyDown: inputEventHandlers.keyDown,
      onFocus: inputEventHandlers.focus,
      onBlur: inputEventHandlers.blur,
      onInputChange: inputEventHandlers.change,
      onRemove: (value, index, event) => {
        // If there is a limit on the maximum number of tags, the parameters passed into InputTag need to be recalculated
        onRemoveCheckedItem && onRemoveCheckedItem(value, index, event);
      }
    };
    return /*#__PURE__*/jsx(InputTag, {
      name: "test"
      // Avoid when clicking outside the browser window, InputTag out of focus
      ,
      theme: {
        root: styles.input_wrapper,
        inner: styles.input_inner,
        default: styles.input_default,
        focus: styles.input_focus,
        tag: styles.tag
      },
      ref: refInput,
      disabled: disabled,
      disableInput: !showSearch,
      placeholder: placeholder,
      value: tagsToShow,
      complexValue: true,
      inputValue: inputValue,
      size: size,
      renderTag: renderTag,
      ...eventHandlers
    });
  }, [disabled, inputEventHandlers, inputValue, maxTagCount, onRemoveCheckedItem, placeholder, refInput, renderTag, showSearch, size, styles, value, optionMap]);
  return {
    renderSingle,
    renderMultiple
  };
};

export { useRenderSelect };
